import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu as MenuIcon } from '@mui/icons-material';
import './style.css';
import { unifiedSidebarData } from '../../constants/unifiedSidebarData';
import WHITE_Logo from '../../assets/images/WHITE__Logo.png';
import { useUser } from '../../utils/hooks/user';
import Sider from 'antd/es/layout/Sider';
import { Menu, Badge } from 'antd';
import appConfig from '../../constants/app';

const SideBar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const roles = useUser((user) => user?.roles);
  const navigate = useNavigate();

  const onClick = useCallback(({ key }) => navigate(key.toLowerCase()), []);

  // Helper function to check if user has required role
  const hasRole = useCallback((requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return roles?.some(role => requiredRoles.includes(role));
  }, [roles]);

  // Filter sidebar items based on user roles
  const sidebars = useMemo(() => {
    const filterMenuItems = (items) => {
      return items
        .filter(item => hasRole(item.roles))
        .map(item => ({
          ...item,
          children: item.children ? filterMenuItems(item.children) : undefined
        }))
        .filter(item => !item.children || item.children.length > 0); // Remove parent items with no visible children
    };

    return filterMenuItems(unifiedSidebarData);
  }, [hasRole]);

  // Highlight the most specific matching menu item based on the current path
  const selectedKeys = useMemo(() => {
    const path = window.location?.pathname?.toLowerCase();
    // Find the most specific (longest) matching key from all sidebar items and their children
    let allKeys = sidebars.flatMap(item => {
      const parentKey = `/${item.route?.toLowerCase() || ''}`;
      if (item.children && item.children.length > 0) {
        return [parentKey, ...item.children.map(child => `/${child.route?.toLowerCase() || ''}`)];
      }
      return [parentKey];
    });
    // Sort keys by length descending to match the most specific route first
    allKeys = allKeys.sort((a, b) => b.length - a.length);
    const matchedKey = allKeys.find(key => path.startsWith(key));
    return matchedKey ? [matchedKey] : ['/'];
  }, [window.location?.pathname, sidebars]);

  const toggleSideBar = () => setCollapsed(!collapsed);

  return (
    <Sider
      breakpoint='lg'
      trigger={null}
      collapsible
      collapsed={collapsed}
      onCollapse={(collapsed) => setCollapsed(collapsed)}
      style={{
        overflow: 'auto',
        position: 'sticky',
        top: 0,
        height: '100vh',
        backgroundColor: '#083147'
      }}
      className='overscroll-none'>
      <div
        className={`flex justify-center items-center w-full ${collapsed ? 'my-5' : 'mt-5 mb-3'}`}>
        {!collapsed ? (
          <div className='flex items-center justify-center'>
            <Link to='/'>
              <img src={WHITE_Logo} alt='logo' width='100px' height='27px' />
            </Link>
            <MenuIcon
              onClick={toggleSideBar}
              className='ml-4 cursor-pointer'
              style={{
                marginBottom: '4%',
                color: '#FFFFFF',
                fontSize: '16px'
              }}
            />
          </div>
        ) : (
          <img
            className='cursor-pointer self-center'
            onClick={toggleSideBar}
            src={'/favicon.ico'}
            alt='logo'
            height='27px'
          />
        )}
      </div>
      <Menu
        onClick={onClick}
        className='mb-6 bg-[#083147]'
        theme='dark'
        mode='inline'
        defaultSelectedKeys={['/']}
        selectedKeys={selectedKeys}
        items={sidebars.map(({ Icon, route, title, children, badge }) => ({
          key: `/${route?.toLowerCase() || ''}`,
          icon: <Icon width={20} height={20} style={{ fontSize: 20 }} />,
          label: (
            <span>
              {title}            {badge && (
              <Badge
                count={badge}
                size='small'
                offset={[-3, -9]}
                fontWeight={600}
                style={{
                  backgroundColor: 'transparent',
                  fontSize: 11,
                  color: '#f5222d',
                  boxShadow: 'none',
                  fontWeight: 'black'
                }}
              />
            )}
            </span>
          ),
          children: children?.map(({ Icon, route, title, badge }) => ({
            key: `/${route?.toLowerCase() || ''}`,
            label: (
              <span>
                {title}
                {badge && (
                  <Badge
                    count={badge}
                    size='small'
                    offset={[0, -5]}
                    fontWeight={600}
                    style={{
                      backgroundColor: 'transparent',
                      fontSize: 14,
                      color: '#f5222d',
                      boxShadow: 'none',
                      fontWeight: 'black'
                    }}
                  />
                )}
              </span>
            ),
            icon: <Icon width={20} height={20} style={{ fontSize: 20 }} />
          }))
        }))}
      />

      <p className='text-center absolute bottom-0 left-0 right-0 text-[#bad5e6]'>
        Version {appConfig.version}
      </p>
    </Sider>
  );
};

export default SideBar;
